import { forwardRef } from "react";

import { cva, type VariantProps } from "class-variance-authority";

import { Slot } from "@radix-ui/react-slot";

const headingVariants = cva(`mb-0 not-italic select-none`, {
  variants: {
    size: {
      1: "text-xs",
      2: "text-base",
      3: "text-md",
      4: "text-xl",
      5: "text-2xl",
      6: "text-4xl",
    },
    color: {
      blue: "text-primary-blue",
      red: "text-primary-red",
      green: "text-primary-green",
      orange: "text-primary-orange",
      gray: "text-primary-gray",
      white: "text-primary-white",
      darkBlue: "text-primary-dark-blue",
      darkGray: "text-primary-dark-gray",
      darkerGray: "text-primary-darker-gray",
    },
    weight: {
      bold: "font-semibold",
      medium: "font-medium",
      regular: "font-normal",
    },
  },
  defaultVariants: {
    size: 5,
    color: "darkBlue",
    weight: "bold",
  },
});

type Tag = "h1" | "h2" | "h3" | "h4" | "h5" | "h6";

interface HeadingProps extends Omit<React.ComponentPropsWithRef<Tag>, "color">, VariantProps<typeof headingVariants> {
  as?: Tag;
  asChild?: boolean;
}

const Heading = forwardRef<HTMLHeadingElement, HeadingProps>((props, forwardedRef) => {
  const { as: Tag = "h1", size, color, weight, className, children, asChild, ...headingProps } = props;
  return (
    <Slot {...headingProps} ref={forwardedRef} className={headingVariants({ size, color, weight, className })}>
      {asChild ? children : <Tag>{children}</Tag>}
    </Slot>
  );
});

Heading.displayName = "Heading";

export { Heading };
export type { HeadingProps, Tag };
